import React, { useState, useEffect, useRef } from 'react';
import { ordersAPI } from '../services/api';

const RecentPurchasesScroll = ({ active = true, barColor }) => {
  const [purchases, setPurchases] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  // Compras novas ficam em fila e só entram quando o loop da animação
  // reinicia (transform em 0) — evita o "pulo"/sumiço dos nomes no meio do scroll
  const pendingRef = useRef([]);
  const replaceRef = useRef(null);

  const applyPending = () => {
    if (replaceRef.current) {
      // Lista fresca do servidor + compras da fila (sem duplicar)
      const replacement = replaceRef.current;
      replaceRef.current = null;
      const novos = pendingRef.current;
      pendingRef.current = [];
      const seen = new Set();
      const merged = [...novos, ...replacement].filter(p => {
        const k = `${p.kako_nickname}-${p.completed_at}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      }).slice(0, 20);
      setPurchases(merged);
      return;
    }
    if (pendingRef.current.length > 0) {
      const novos = pendingRef.current;
      pendingRef.current = [];
      setPurchases(prev => [...novos, ...prev].slice(0, 20));
    }
  };

  useEffect(() => {
    if (!active) return;
    
    // Carrega dados iniciais (fallback)
    loadRecentPurchases();
    
    // Conecta WebSocket para atualizações em tempo real
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [active]);

  const connectWebSocket = () => {
    try {
      const wsUrl = process.env.REACT_APP_BACKEND_URL
        .replace('https://', 'wss://')
        .replace('http://', 'ws://');
      
      const ws = new WebSocket(`${wsUrl}/api/ws/recent-purchases`);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('🔌 RecentPurchases WebSocket conectado');
      };
      
      ws.onmessage = (event) => {
        if (event.data === 'pong') return; // keepalive, não é JSON
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'initial_data') {
            // Dados iniciais: aplica direto se a barra ainda está vazia;
            // se já está rolando, troca apenas no reinício do loop
            setPurchases(prev => {
              if (prev.length === 0) return data.purchases || [];
              replaceRef.current = data.purchases || [];
              return prev;
            });
          } else if (data.type === 'new_purchase') {
            // Nova compra: entra na fila e aparece no próximo reinício do loop
            pendingRef.current = [data.purchase, ...pendingRef.current];
          }
        } catch (e) {
          console.error('Erro ao processar mensagem WS:', e);
        }
      };
      
      ws.onclose = () => {
        console.log('🔌 RecentPurchases WebSocket desconectado');
        // Reconecta após 5 segundos
        reconnectTimeoutRef.current = setTimeout(() => {
          if (active) connectWebSocket();
        }, 5000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      // Ping a cada 30 segundos para manter conexão
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
      
      ws.addEventListener('close', () => clearInterval(pingInterval));
      
    } catch (error) {
      console.error('Erro ao conectar WebSocket:', error);
      // Fallback para polling se WebSocket falhar
      const interval = setInterval(loadRecentPurchases, 30000);
      return () => clearInterval(interval);
    }
  };

  const loadRecentPurchases = async () => {
    try {
      const response = await ordersAPI.getRecentPurchases(20);
      setPurchases(prev => {
        if (prev.length === 0) return response.data;
        replaceRef.current = response.data;
        return prev;
      });
    } catch (error) {
      console.error('Erro ao carregar compras recentes:', error);
    }
  };

  if (purchases.length === 0 || !active) return null;

  // Garante largura mínima (evita buracos com poucas compras) e
  // duplica a faixa para o loop contínuo de -50%
  const repeats = Math.max(1, Math.ceil(8 / purchases.length));
  const filled = Array.from({ length: repeats }).flatMap(() => purchases);
  const duplicatedPurchases = [...filled, ...filled];

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 overflow-hidden shadow-lg"
         style={barColor ? { background: barColor } : undefined}
         data-testid="buyers-bar">
      {barColor && barColor.includes('gradient') && <div className="bar-light-sweep" />}
      <div className="animate-marquee whitespace-nowrap inline-block"
           onAnimationIteration={applyPending}>
        {duplicatedPurchases.map((purchase, index) => (
          <span key={`${purchase.completed_at}-${index}`} className="inline-flex items-center gap-2 mx-6">
            {purchase.kako_avatar ? (
              <img
                src={purchase.kako_avatar}
                alt={purchase.kako_nickname}
                className="w-6 h-6 rounded-full border border-white inline-block"
              />
            ) : (
              <span>👤</span>
            )}
            <span className="font-bold">"{purchase.kako_nickname}"</span>
            <span>acabou de comprar! 💰🤝🏻</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default RecentPurchasesScroll;
